/**
 * Apple Ads：自定义盯盘策略配置 API 服务（CLAUDE.md §前端 API 服务层 — 按域分目录）。
 *
 * 与后端 Pydantic 模型 apple_asa.dtos.watch_strategy 对齐。
 *   - GET  /api/v1/apple-asa/watch-strategy?page=&size= → 策略分页列表
 *     （任意登录用户可读，按 id 倒序）
 *   - POST /api/v1/apple-asa/watch-strategy → 新建策略（仅 v_summerxiao /
 *     reizhu / v_hooverhuang / admin / devinzhang，否则后端 403；入参不合法 400；
 *     写库失败 500；新建默认打开）
 *   - PUT  /api/v1/apple-asa/watch-strategy/{id} → 编辑条件组与操作
 *     （不动 create_user / is_enabled）
 *   - PUT  /api/v1/apple-asa/watch-strategy/{id}/status → 打开 / 关闭策略
 *   - DELETE /api/v1/apple-asa/watch-strategy/{id} → 软删除策略
 *     （is_deleted=1 并同时置 is_enabled=0；仅白名单用户，否则后端 403）
 *
 * 生效链路：写入的配置由后端判断引擎脚本（apple-watch-strategy，周期性外部调度）
 * 读取 is_enabled=1 的策略做关键词粒度判断，命中结果落库 t_apple_watch_strategy_result
 * （本轮只判断 + 存储，不调 Apple API 执行改价/暂停）。
 */

import { request } from "../apiClient";

/** 指标类别：numeric=数值/衍生指标（须带时间周期）；attribute=属性指标（无时间周期）。 */
export type MetricType = "numeric" | "attribute";

/**
 * 阈值模式（与后端 dtos.watch_strategy 一致，PR-AT-20260923-009；011 起扩至 total_sxfl；
 * 013 起 cost_ent_appl 增「目标成本×x」）：
 * fixed=固定值（默认，存量条件无此字段按此口径）；
 * target_sxfl_multiplier=「目标费率的 x 倍」——生效阈值 = 目标费率 × value
 * （x 存于 value，须为正数），仅授信费率类指标（sxfl / total_sxfl）可用；
 * target_cost_multiplier=「目标成本的 x 倍」——生效阈值 = 目标成本 cost_value（元）× value，
 * 仅企业进件成本指标（cost_ent_appl）可用（PR-AT-20260923-013）。
 */
export type ValueMode = "fixed" | "target_sxfl_multiplier" | "target_cost_multiplier";

/** 阈值模式枚举值（与后端 VALUE_MODE_FIXED / VALUE_MODE_TARGET_SXFL_MULTIPLIER /
 * VALUE_MODE_TARGET_COST_MULTIPLIER 一致）。 */
export const VALUE_MODE_FIXED: ValueMode = "fixed";
export const VALUE_MODE_TARGET_SXFL_MULTIPLIER: ValueMode = "target_sxfl_multiplier";
export const VALUE_MODE_TARGET_COST_MULTIPLIER: ValueMode = "target_cost_multiplier";

/** 授信费率指标 key（时间窗费率，支持「目标费率×x」模式，与后端 SXFL_METRIC_KEY 一致）。 */
export const SXFL_METRIC_KEY = "sxfl";
/** 当月整体授信费率指标 key（属性指标，支持「目标费率×x」模式，与后端 TOTAL_SXFL_METRIC_KEY 一致）。 */
export const TOTAL_SXFL_METRIC_KEY = "total_sxfl";
/** 允许「目标费率×x」倍率模式的指标集合（与后端 MULTIPLIER_ALLOWED_METRICS 双源一致）。 */
export const MULTIPLIER_ALLOWED_METRICS: readonly string[] = [
  SXFL_METRIC_KEY,
  TOTAL_SXFL_METRIC_KEY,
];

/** 企业进件成本指标 key（衍生指标，支持「目标成本×x」模式，与后端 COST_ENT_APPL_METRIC_KEY 一致）。 */
export const COST_ENT_APPL_METRIC_KEY = "cost_ent_appl";
/** 允许「目标成本×x」倍率模式的指标集合（与后端 COST_MULTIPLIER_ALLOWED_METRICS 双源一致）。 */
export const COST_MULTIPLIER_ALLOWED_METRICS: readonly string[] = [
  COST_ENT_APPL_METRIC_KEY,
];

/** 单个条件（与后端 ConditionItem 对齐；属性指标 period 必为 null）。 */
export interface ConditionItem {
  metric_type: MetricType;
  /** 时间周期（数值/衍生指标必填，取值见 PERIOD_OPTIONS；属性指标为 null）。 */
  period: string | null;
  /** 指标 key（取值见 METRIC_OPTIONS；service 层校验与 metric_type 联动）。 */
  metric: string;
  operator: string;
  /** 数值/衍生/数值属性指标为数值；多值枚举（present_hour，见
   * MULTI_VALUE_ENUM_METRIC_KEYS）为枚举字符串数组（非空子集，
   * = 读作 ∈、!= 读作 ∉，PR-AT-20261023-001）。 */
  value: number | string[];
  /** 阈值模式（sxfl / total_sxfl 可选 target_sxfl_multiplier；cost_ent_appl 可选
   * target_cost_multiplier；缺省按 fixed 口径）。 */
  value_mode?: ValueMode;
}

/** 盯盘策略当前态（与后端 WatchStrategyRecord 对齐；None 字段经 exclude_none 不下发）。 */
export interface WatchStrategyRecord {
  id: number;
  conditions: ConditionItem[];
  action_type: "reduce_bid" | "increase_bid" | "pause_keyword" | "alert";
  /** 操作参数（reduce_bid/increase_bid=百分比小数，如 0.10=10%；pause_keyword/alert 不下发）。 */
  action_value?: number;
  is_enabled: boolean;
  create_user?: string;
  modify_user?: string;
  created_time?: string;
  updated_time?: string;
}

/** 新建 / 编辑入参（POST / PUT）。 */
export interface SaveWatchStrategyBody {
  /** 条件组（1~5 组，组内 AND）。 */
  conditions: ConditionItem[];
  action_type: "reduce_bid" | "increase_bid" | "pause_keyword" | "alert";
  /** 调/提价必填（(0,1) 开区间百分比小数）；pause_keyword / alert 必须为 null。 */
  action_value: number | null;
}

/** 策略分页列表出参。 */
export interface WatchStrategyListResult {
  total: number;
  items: WatchStrategyRecord[];
}

/** 读取盯盘策略分页列表（任意登录用户可读；默认拉前 200 条全量回显）。 */
export async function listWatchStrategies(
  page = 1,
  size = 200,
): Promise<WatchStrategyListResult> {
  return request<WatchStrategyListResult>(
    `/api/v1/apple-asa/watch-strategy?page=${page}&size=${size}`,
  );
}

/** 新建盯盘策略（默认打开）；返回建后当前态。非白名单用户后端返回 403。 */
export async function createWatchStrategy(
  body: SaveWatchStrategyBody,
): Promise<WatchStrategyRecord> {
  return request<WatchStrategyRecord>("/api/v1/apple-asa/watch-strategy", {
    method: "POST",
    body,
  });
}

/** 编辑盯盘策略条件组与操作（不动开关态与首次创建人）；返回改后当前态。 */
export async function updateWatchStrategy(
  id: number,
  body: SaveWatchStrategyBody,
): Promise<WatchStrategyRecord> {
  return request<WatchStrategyRecord>(`/api/v1/apple-asa/watch-strategy/${id}`, {
    method: "PUT",
    body,
  });
}

/** 打开 / 关闭盯盘策略；返回改后当前态。 */
export async function toggleWatchStrategy(
  id: number,
  isEnabled: boolean,
): Promise<WatchStrategyRecord> {
  return request<WatchStrategyRecord>(
    `/api/v1/apple-asa/watch-strategy/${id}/status`,
    {
      method: "PUT",
      body: { is_enabled: isEnabled },
    },
  );
}

/** 软删除盯盘策略（is_deleted=1 且 is_enabled=0）；返回被删策略 id。 */
export async function deleteWatchStrategy(id: number): Promise<{ id: number }> {
  return request<{ id: number }>(`/api/v1/apple-asa/watch-strategy/${id}`, {
    method: "DELETE",
  });
}

/* ---------- 注册表中文映射（与后端 apple_asa.dtos.watch_strategy 严格一致） ----------
 * 编辑器下拉选项与列表「满足条件 / 执行操作」中文渲染共用同一来源，避免双源漂移。 */

export interface LabelOption {
  value: string;
  label: string;
}

/** 时间窗（10 个，与后端 PERIODS 一致；属性指标无时间周期）。 */
export const PERIOD_OPTIONS: LabelOption[] = [
  { value: "today", label: "今日" },
  { value: "3d", label: "近3个工作日(不含周末)" },
  { value: "3d_cont", label: "近3个自然日" },
  { value: "5d", label: "近5个工作日(不含周末)" },
  { value: "5d_cont", label: "近5个自然日" },
  { value: "10d", label: "近10个工作日(不含周末)" },
  { value: "10d_cont", label: "近10个自然日" },
  { value: "20d", label: "近20个工作日(不含周末)" },
  { value: "20d_cont", label: "近20个自然日" },
  { value: "mon", label: "本月至今" },
];

/** 数值 + 衍生指标（15 个，编辑器「数值指标」下拉一组展示，与后端 NUMERIC_METRICS + DERIVED_METRICS 一致）。 */
export const NUMERIC_METRIC_OPTIONS: LabelOption[] = [
  { value: "consume", label: "消耗" },
  { value: "totalInstalls", label: "安装次数(总计)" },
  { value: "tapInstalls", label: "安装次数(经点击)" },
  { value: "register", label: "注册" },
  { value: "sign_cnt", label: "签约" },
  { value: "ent_appl_cnt", label: "企业进件" },
  { value: "credit_cnt", label: "授信数" },
  { value: "credit_amt", label: "授信金额(万)" },
  { value: "cost_total_installs", label: "安装次数(总计)成本" },
  { value: "cost_tap_installs", label: "安装次数(经点击)成本" },
  { value: "cost_register", label: "注册成本" },
  { value: "cost_sign", label: "签约成本" },
  { value: "cost_ent_appl", label: "企业进件成本" },
  { value: "cost_credit", label: "授信成本" },
  { value: "sxfl", label: "授信费率" },
];

/** 属性指标（5 个，无时间周期，与后端 ATTRIBUTE_METRICS 一致）。
 *  注：present_hour 已迁移至 ENUM_ATTRIBUTE_METRIC_OPTIONS
 * （"0"~"23" 多值枚举，PR-AT-20261023-001）。 */
export const ATTRIBUTE_METRIC_OPTIONS: LabelOption[] = [
  { value: "total_sxfl", label: "苹果商店当月整体授信费率" },
  { value: "lowImpressionShare", label: "SOV下限" },
  { value: "highImpressionShare", label: "SOV上限" },
  { value: "target_sxfl", label: "目标授信费率" },
  { value: "present_day", label: "当月天日期" },
];

/** 枚举属性指标（无时间周期，多值枚举，仅支持 = / !=，读作 ∈ / ∉，
 *  与后端 ENUM_ATTRIBUTE_METRICS 一致）。 */
export const ENUM_ATTRIBUTE_METRIC_OPTIONS: LabelOption[] = [
  { value: "present_hour", label: "当天小时段" },
];

/** present_hour 合法枚举值："0"~"23"（与后端 PRESENT_HOUR_VALUES 一致）。 */
export const PRESENT_HOUR_VALUES: readonly string[] = Array.from(
  { length: 24 },
  (_, h) => String(h),
);

/** 枚举指标 key → 合法值清单（与后端 ENUM_METRIC_VALUES 一致，预留扩展）。 */
export const ENUM_METRIC_VALUES: Record<string, readonly string[]> = {
  present_hour: PRESENT_HOUR_VALUES,
};

/** 枚举属性指标仅支持的操作符（与后端 ENUM_OPERATORS 一致；
 *  多值枚举上 = 读作 ∈、!= 读作 ∉）。 */
export const ENUM_OPERATOR_OPTIONS: readonly string[] = ["=", "!="];

/** 多值枚举指标 key 集合（与后端 MULTI_VALUE_ENUM_METRICS 一致）：
 *  value 为枚举值字符串数组（非空子集）；编辑器以多选 chips 承载。 */
export const MULTI_VALUE_ENUM_METRIC_KEYS: ReadonlySet<string> = new Set([
  "present_hour",
]);

/** 操作符（6 个，复用华为页集合，与后端 OPERATORS 一致）。 */
export const OPERATOR_OPTIONS: string[] = ["=", "!=", ">", ">=", "<", "<="];

/** 阈值模式选项（仅 sxfl / total_sxfl 授信费率类条件行展示，与后端 VALUE_MODES 一致）。 */
export const VALUE_MODE_OPTIONS: LabelOption[] = [
  { value: VALUE_MODE_FIXED, label: "固定值" },
  { value: VALUE_MODE_TARGET_SXFL_MULTIPLIER, label: "目标费率×" },
];

/** 成本倍率阈值模式选项（仅 cost_ent_appl 企业进件成本条件行展示，PR-AT-20260923-013）。 */
export const COST_VALUE_MODE_OPTIONS: LabelOption[] = [
  { value: VALUE_MODE_FIXED, label: "固定值" },
  { value: VALUE_MODE_TARGET_COST_MULTIPLIER, label: "目标成本×" },
];

/** 操作类型（4 个，与后端 ACTION_TYPES 一致；预警仅记录命中供后续告警，无参数）。 */
export const ACTION_TYPE_OPTIONS: LabelOption[] = [
  { value: "reduce_bid", label: "出价降低" },
  { value: "increase_bid", label: "出价提升" },
  { value: "pause_keyword", label: "暂停投放" },
  { value: "alert", label: "预警" },
];

/** 单策略条件组上限（与后端 MAX_CONDITIONS 一致）。 */
export const MAX_CONDITIONS = 5;

const PERIOD_LABELS = new Map(PERIOD_OPTIONS.map((o) => [o.value, o.label]));
const METRIC_LABELS = new Map(
  [...NUMERIC_METRIC_OPTIONS, ...ATTRIBUTE_METRIC_OPTIONS, ...ENUM_ATTRIBUTE_METRIC_OPTIONS].map((o) => [o.value, o.label]),
);
const ACTION_TYPE_LABELS = new Map(ACTION_TYPE_OPTIONS.map((o) => [o.value, o.label]));

/** 生效阈值格式化：至多 4 位小数去尾零（0.012000000000002 → "0.012"）。 */
export function formatThreshold(v: number): string {
  return String(Math.round(v * 10000) / 10000);
}

/**
 * 单条件中文描述（列表「满足条件」列展示，不含实际值）：
 * 数值/衍生指标带周期前缀「近3个工作日(不含周末)·注册成本>50」；属性指标无前缀「SOV下限<0.2」。
 * 多值枚举（present_hour）渲染为「当天小时段∈{14,15}」（operator != 渲染 ∉，
 * PR-AT-20261023-001）。
 * value_mode='target_sxfl_multiplier'（sxfl / total_sxfl）时阈值段渲染为「目标费率×{x}」，
 * 传入 targetSxfl（当前目标费率）时追加生效阈值「(=0.012)」；
 * value_mode='target_cost_multiplier'（cost_ent_appl，PR-AT-20260923-013）时渲染为
 * 「目标成本×{x}」，传入 targetCost（当前目标成本，元）时追加生效阈值「(=6000)」。
 */
export function describeCondition(
  c: ConditionItem,
  targetSxfl?: number,
  targetCost?: number,
): string {
  const metricLabel = METRIC_LABELS.get(c.metric) ?? c.metric;
  const prefix =
    c.metric_type !== "attribute" && c.period
      ? `${PERIOD_LABELS.get(c.period) ?? c.period}·`
      : "";
  if (Array.isArray(c.value)) {
    const opText = c.operator === "!=" ? "∉" : "∈";
    return `${prefix}${metricLabel}${opText}{${c.value.join(",")}}`;
  }
  if (c.value_mode === VALUE_MODE_TARGET_SXFL_MULTIPLIER) {
    const effective =
      targetSxfl !== undefined ? `(=${formatThreshold(targetSxfl * c.value)})` : "";
    return `${prefix}${metricLabel}${c.operator}目标费率×${c.value}${effective}`;
  }
  if (c.value_mode === VALUE_MODE_TARGET_COST_MULTIPLIER) {
    const effective =
      targetCost !== undefined ? `(=${formatThreshold(targetCost * c.value)})` : "";
    return `${prefix}${metricLabel}${c.operator}目标成本×${c.value}${effective}`;
  }
  return `${prefix}${metricLabel}${c.operator}${c.value}`;
}

/**
 * 单条件文案的「前缀/数值/后缀」拆段（脑图数值内联编辑用，与 describeCondition
 * 同口径：prefix + valueText + suffix === describeCondition(c, targetSxfl, targetCost)）。
 * 仅数值可编辑条件（value 为 number：数值/衍生/属性指标）返回非 null；
 * 多值枚举（present_hour，value 为 string[]）返回 null。
 * 倍率模式：prefix 含「目标费率×」/「目标成本×」，valueText 为倍数 x，suffix 为
 * 基线生效值「(=0.012)」（基线未传时为空）。
 */
export function describeConditionParts(
  c: ConditionItem,
  targetSxfl?: number,
  targetCost?: number,
): { prefix: string; valueText: string; suffix: string } | null {
  if (typeof c.value !== "number") return null;
  const metricLabel = METRIC_LABELS.get(c.metric) ?? c.metric;
  const prefix =
    c.metric_type !== "attribute" && c.period
      ? `${PERIOD_LABELS.get(c.period) ?? c.period}·`
      : "";
  if (c.value_mode === VALUE_MODE_TARGET_SXFL_MULTIPLIER) {
    const effective =
      targetSxfl !== undefined ? `(=${formatThreshold(targetSxfl * c.value)})` : "";
    return {
      prefix: `${prefix}${metricLabel}${c.operator}目标费率×`,
      valueText: String(c.value),
      suffix: effective,
    };
  }
  if (c.value_mode === VALUE_MODE_TARGET_COST_MULTIPLIER) {
    const effective =
      targetCost !== undefined ? `(=${formatThreshold(targetCost * c.value)})` : "";
    return {
      prefix: `${prefix}${metricLabel}${c.operator}目标成本×`,
      valueText: String(c.value),
      suffix: effective,
    };
  }
  return {
    prefix: `${prefix}${metricLabel}${c.operator}`,
    valueText: String(c.value),
    suffix: "",
  };
}

/**
 * 操作中文描述（列表「执行操作」列展示）：
 * 「出价降低 10%」/「出价提升 20%」/「暂停投放」/「预警」。
 */
export function describeAction(
  actionType: string,
  actionValue: number | null | undefined,
): string {
  const base = ACTION_TYPE_LABELS.get(actionType) ?? actionType;
  if (actionType === "reduce_bid" || actionType === "increase_bid") {
    return `${base} ${Math.round((actionValue ?? 0) * 100)}%`;
  }
  return base;
}

/**
 * 操作文案的「前缀/数值/后缀」拆段（脑图数值内联编辑用，与 describeAction 同口径：
 * prefix + valueText + suffix === describeAction(actionType, actionValue)）。
 * 仅带数值参数的操作（出价降低/提升）返回非 null；暂停投放/预警返回 null。
 * valueText 口径：百分比数字（0.10 → "10"，与展示一致）。
 */
export function describeActionParts(
  actionType: string,
  actionValue: number | null | undefined,
): { prefix: string; valueText: string; suffix: string } | null {
  const base = ACTION_TYPE_LABELS.get(actionType) ?? actionType;
  if (actionType === "reduce_bid" || actionType === "increase_bid") {
    return {
      prefix: `${base} `,
      valueText: String(Math.round((actionValue ?? 0) * 100)),
      suffix: "%",
    };
  }
  return null;
}
